import { useMemo } from 'react';

import { FEATURED_SET_EVENT } from 'app/shared/utils/events';
import {
  FeeType,
  PriceBreakdown,
  PriceBreakdownProps,
} from 'app/fan/utils/checkout/typings';
import {
  calculateDiscountAmount,
  calculateNumberOfFreeTickets,
} from 'app/fan/utils/promoCodes';

import { calculateEventFee } from './calculateEventFee';
import { calculateFinalPrice } from './calculateFinalPrice';

export const usePriceBreakdown = ({
  numberOfTickets,
  promoCodeWithCharges,
  numberOfVisaFreeTickets,
  canRedeemAndIsRedeemingVisaFreeTicket,
  excludeFeesFromCalculation = false,
  event,
  eventAttendee,
}: PriceBreakdownProps) => {
  const currencySymbol = event?.city?.country?.currencySymbol || '';
  const eventPrice =
    eventAttendee?.skipPayment === true ? 0 : event?.price || 0;
  const earlyBirdOriginalPrice = event?.isEarlyBird
    ? eventAttendee?.skipPayment === true
      ? 0
      : event?.earlyBirdOriginalPrice
    : undefined;

  const visaDiscountAmount = canRedeemAndIsRedeemingVisaFreeTicket
    ? eventPrice
    : 0;
  const promoCodeDiscountAmount = calculateDiscountAmount(
    eventPrice,
    numberOfTickets,
    promoCodeWithCharges
  );

  const isFeaturedSetEvent = event?.type === FEATURED_SET_EVENT;
  const feeType: FeeType = isFeaturedSetEvent ? 'serviceFee' : 'bookingFee';
  const fee = excludeFeesFromCalculation
    ? 0
    : calculateEventFee(event, eventAttendee);
  const numberOfFreeTickets = promoCodeWithCharges
    ? calculateNumberOfFreeTickets(promoCodeWithCharges)
    : numberOfVisaFreeTickets;
  const numberOfTicketsWithFee = numberOfTickets - numberOfFreeTickets;

  const finalPrice = calculateFinalPrice({
    event,
    eventAttendee,
    numberOfTickets,
    numberOfFreeTickets,
    fee,
    promoCodeIsValid: promoCodeWithCharges?.isValid,
    promoCodeDiscountAmount,
    visaDiscountAmount,
  });

  const priceBreakdown = useMemo<PriceBreakdown>(
    () => ({
      currencySymbol,
      eventPrice,
      earlyBirdOriginalPrice,
      feeType,
      fee,
      finalPrice,
      numberOfTicketsWithFee,
      visaDiscountAmount,
      promoCodeDiscountAmount,
    }),
    [
      currencySymbol,
      eventPrice,
      earlyBirdOriginalPrice,
      feeType,
      fee,
      finalPrice,
      numberOfTicketsWithFee,
      visaDiscountAmount,
      promoCodeDiscountAmount,
    ]
  );

  return priceBreakdown;
};
